import React from 'react';
import {getEntryScopeProps} from '@tlg-contenthub/live-preview';
import componentsConfigsMgr from 'contentful/componentsConfigsMgr';
import {v4 as uuid} from 'uuid';

import {isSyntheticEntryId} from 'coreUtils/livePreview';
import {withTryCatch} from 'coreUtils/reactUtils';
import {cloneObject} from 'coreUtils/utils';

/**
 * Content type of a grouped reference. A field configured as grouped holds these ordering
 * wrappers instead of entries, on both API versions.
 */
const REF_GROUP_TYPENAME = 'ReferencesGroup';

/** Content type of an asset: its `sys.id` is not an entry id. */
const ASSET_TYPENAME = 'Asset';

interface IContentfulConfig extends React.Attributes {
	__typename?: string;
	sys?: {
		id?: string;
	};
	itemsCollection?: {items?: object[]};
	children?: TBuildReturn[];
	content?: {
		json?: unknown;
		data?: unknown;
	};
}

type TBuildReturn =
	| React.DetailedReactHTMLElement<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
	| IContentfulConfig
	| null;

/** All the builder needs to know about a group: it holds the entries to build. */
interface IBuildableGroup {
	items: unknown[];
}

/**
 * Represents ComponentsBuilder
 */
class ComponentsBuilder {
	isPreviewMode: boolean = false;

	/**
	 * Contentful ComponentsBuilder
	 * @param previewMode
	 */
	constructor(previewMode: boolean = false) {
		this.isPreviewMode = previewMode;
	}

	/**
	 * Builds the children of a grouped reference into a nested array, which React flattens on
	 * render while the grouping stays readable to whoever wants to wrap each group.
	 * The group id and title are dropped: no consumer renders them.
	 * Groups cannot nest (rejected by the CMS content type validation), so their items are
	 * built directly and no group is ever met again further down. Anything the items hold is
	 * left to `buildReactTree`, which already handles it the same way as in a flat field.
	 * @param group Grouped reference to build
	 * @returns {TBuildReturn[]}
	 */
	private buildRefGroup(group: IBuildableGroup): TBuildReturn[] {
		const reactElements: TBuildReturn[] = [];

		group.items.forEach((item) => {
			const reactElement = this.buildReactTree(item as IContentfulConfig);

			if (reactElement) {
				reactElements.push(reactElement);
			}
		});

		return reactElements;
	}

	/**
	 * Builds a React components tree according to the passed config returned by Contentful API Client
	 * @param contentfulResponse Config to build tree from (Content Management System Data)
	 * @returns {React.FC|null}
	 */
	private buildReactTree(contentfulResponse: IContentfulConfig): TBuildReturn {
		// If the passed data is not an object, return null
		if (!contentfulResponse || typeof contentfulResponse !== 'object') {
			return null;
		}

		// If the passed data matches a configuration in componentsConfigsMgr, return it as is
		if (componentsConfigsMgr.isConfiguration(contentfulResponse.__typename)) {
			return contentfulResponse;
		}

		// Add a unique key to the passed data
		const prependKey = uuid();

		// Keep the children key to not cause a breaking change
		// TODO: Remove this in the next major release.
		// the children key was intended to be used for models that have only one collection
		// it won't make sense to combine all the data of the nested collections into one key!
		const children = [];

		// Get the keys of the passed data
		const contentfulResponseKeys = Object.keys(contentfulResponse || {});

		contentfulResponseKeys.forEach((key) => {
			const value = contentfulResponse[key];

			// If the value is null, skip to the next iteration
			if (value === null) {
				return;
			}

			if (componentsConfigsMgr.shouldKeepAsData?.(contentfulResponse.__typename, key)) {
				return;
			}

			const isCollection = key.endsWith('Collection');
			const isObject = typeof value === 'object';
			const hasItems = isObject && Array.isArray(value.items) && value.items.length;

			// If it's a collection, we need to iterate over it and build each item
			// then assign the result to the reactElements key
			if (isCollection && isObject && hasItems) {
				if (Object.isExtensible(contentfulResponse[key]) === false) {
					console.error('Object is not extensible', contentfulResponse[key]);

					return;
				}

				contentfulResponse[key].reactElements = [];

				value.items.forEach((item) => {
					// A grouped field holds groups instead of entries. Each group becomes a nested
					// array, deliberately left out of the flat `children` array, which would lose it.
					if (item?.__typename === REF_GROUP_TYPENAME) {
						contentfulResponse[key].reactElements.push(this.buildRefGroup(item));

						return;
					}

					const reactElement = this.buildReactTree(item);

					if (reactElement) {
						contentfulResponse[key].reactElements.push(reactElement);
						children.push(reactElement);
					}
				});
			} else if (isObject && value.__typename) {
				try {
					contentfulResponse[key] = this.buildReactTree(value);
				} catch (e) {
					console.error('Contentful component error:', value);
				}
			} else if (isObject && ((!!value.sys && !value.__typename) || Object.keys(value).length === 0)) {
				delete contentfulResponse[key];
			}
		});

		const key = contentfulResponse?.sys?.id ? `${contentfulResponse.sys.id}|${prependKey}` : prependKey;
		const Component = withTryCatch(componentsConfigsMgr.get(contentfulResponse.__typename as any));

		if (children.length > 0) {
			contentfulResponse.children = children;
		}

		// Old content specific logic not architecturally correct and not available and in v2 content hub responses
		if (contentfulResponse.__typename === 'Text') {
			if (contentfulResponse.content && !contentfulResponse.content.json && contentfulResponse.content.data) {

				contentfulResponse.content = {json: contentfulResponse.content};
			}
		}

		const entryId = contentfulResponse?.sys?.id;
		const isEditableEntry = !!entryId && !isSyntheticEntryId(entryId) && contentfulResponse.__typename !== ASSET_TYPENAME;

		if (this.isPreviewMode && isEditableEntry) {
			// Tagged on the element itself, not on a wrapper: consumers read the `.props` of built elements.
			const entryScope = getEntryScopeProps({id: entryId, type: contentfulResponse.__typename});

			return <Component {...contentfulResponse} key={key} {...entryScope} />;
		}

		return <Component {...contentfulResponse} key={key} />;
	}

	/**
	 * Clones the passed data (to avoid mutating the original) and builds a react components tree
	 * @param {Object} contentfulResponse Config to build tree from (Content Management System Data)
	 * @param {Boolean} isLivePreview - true to enable the live preview
	 * @param {Boolean} cloneSource - true to clone the source object
	 * @returns {React.FC|null}
	 */
	build(contentfulResponse: IContentfulConfig, cloneSource: boolean = false): TBuildReturn {
		return this.buildReactTree(cloneSource ? cloneObject(contentfulResponse) : contentfulResponse);
	}
}

export default ComponentsBuilder;
